<template>
  <div :class="['bf-shell', { 'bf-shell--auth': !shouldShowSidebar }]">
    <MainSidebar v-if="shouldShowSidebar" />

    <!-- Backdrop mobile ao abrir a sidebar -->
    <div
      v-if="shouldShowSidebar && stateStoreInstance.open"
      class="bf-backdrop"
      @click="stateStoreInstance.onChange"
    ></div>

    <div
      :class="[
        'main-content d-flex flex-column',
        { 'with-sidebar': shouldShowSidebar },
      ]"
    >
      <MainHeader v-if="shouldShowHeader" />

      <router-view />

      <div class="flex-grow-1"></div>
    </div>

    <SuccessSnackbar />

    <LoadingDialog />

    <AlertComponent
      v-if="alertStoreInstance.isVisible"
      style="position: fixed; right: 15px; bottom: 0px"
      :message="alertStoreInstance.message"
      :type="alertStoreInstance.type"
      @close-alert="
        () => (alertStoreInstance.isVisible = !alertStoreInstance.isVisible)
      "
    />

    <!-- Modal global: pagamento pendente -->
    <PaymentRequiredModal />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  watchEffect,
  onMounted,
  onBeforeUnmount,
  ref,
} from "vue";

import { useRoute } from "vue-router";

import stateStore from "@/utils/store";
import { startTokenExpirationWatcher } from "@/utils/tokenWatcher";

import { useAuthStore } from "@/stores/auth.store";
import { useAlertStore } from "@/stores/alert.store";

import { useAuth } from "./composables/useAuth";
import { useUser } from "@/composables/useUser";

import MainHeader from "@/components/Layouts/MainHeader.vue";
import MainSidebar from "@/components/Layouts/MainSidebar.vue";

import LoadingDialog from "@/components/Dialog/LoadingDialog.vue";
import SuccessSnackbar from "@/components/Snackbar/SuccessSnackbar.vue";

import AlertComponent from "@/components/Alerts/AlertComponent.vue";
import PaymentRequiredModal from "@/components/Subscription/PaymentRequiredModal.vue";

export default defineComponent({
  name: "App",
  components: {
    MainHeader,
    MainSidebar,
    LoadingDialog,
    SuccessSnackbar,
    AlertComponent,
    PaymentRequiredModal,
  },
  mounted() {
    document.body.classList.add("bg-body-secondary");
  },
  setup() {
    const route = useRoute();

    const alertStoreInstance = useAlertStore();
    const authStore = useAuthStore();

    const stateStoreInstance = stateStore;

    const { refreshToken, logout } = useAuth();
    const { getUserData } = useUser();

    const isRefreshing = ref(false);

    const hiddenRoutes = [
      // "/",
      "/error-500",
      "/coming-soon",
      "/autenticar/login",
      "/authentication/logout",
      "/authentication/register",
      "/authentication/signin-signup",
      "/authentication/forgot-password",
      "/authentication/reset-password",
      "/authentication/confirm-mail",
      "/authentication/lock-screen",
      "/conta-ativada",
    ];

    const shouldShowSidebar = computed(
      () => !hiddenRoutes.includes(route.path) && authStore.getToken
    );
    const shouldShowHeader = computed(
      () => !hiddenRoutes.includes(route.path) && authStore.getToken
    );
    const shouldShowFooter = computed(
      () => !hiddenRoutes.includes(route.path) && authStore.getToken
    );

    // Função para lidar com a expiração do token
    const handleTokenExpiration = async () => {
      if (isRefreshing.value) {
        return;
      }

      try {
        isRefreshing.value = true;

        const response = await refreshToken();

        if (response) {
          console.log("[App] Refresh bem-sucedido.");
          // Não reagenda - apenas 1 refresh por sessão
        } else {
          console.error("[App] Refresh falhou, fazendo logout");
          const logoutResponse = await logout();
          if (logoutResponse) {
            // router.push({ name: "SignInPage" }).then(() => {
            //   router.go(0);
            // });
          }
        }
      } catch (error) {
        console.error("[App] Erro ao renovar o token:", error);
      } finally {
        isRefreshing.value = false;
      }
    };

    watchEffect(() => {
      if (stateStoreInstance.open) {
        document.body.classList.remove("sidebar-show");
        document.body.classList.add("sidebar-hide");
      } else {
        document.body.classList.remove("sidebar-hide");
        document.body.classList.add("sidebar-show");
      }
    });

    onMounted(() => {
      const isLoggedIn = authStore.getToken;

      if (isLoggedIn) {
        const expiresAt = authStore.getExpiresAt;

        if (expiresAt) {
          startTokenExpirationWatcher();
        }

        // Garante que os dados do usuário estejam carregados (para checar 'paid')
        const user = authStore.getUser as any;
        if (!user || (typeof user === "string" && user === "")) {
          // Chama sem aguardar, apenas garante que o store será preenchido
          try {
            getUserData();
          } catch (e) {
            /* ignore */
          }
        }
      }

      // Adiciona o listener do evento quando o componente é montado
      window.addEventListener("token-expiration-warning", handleTokenExpiration);
    });

    onBeforeUnmount(() => {
      // Remove o listener do evento quando o componente é desmontado
      window.removeEventListener(
        "token-expiration-warning",
        handleTokenExpiration
      );
    });

    return {
      stateStoreInstance,
      alertStoreInstance,
      shouldShowSidebar,
      shouldShowHeader,
      shouldShowFooter,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-shell {
  min-height: 100vh;
  background: var(--bg);
}

.main-content {
  min-height: 100vh;
  transition: padding 0.25s ease;

  &.with-sidebar {
    padding-left: 248px;
  }
}

.bf-backdrop {
  display: none;
}

@media only screen and (max-width: 991px) {
  .main-content.with-sidebar {
    padding-left: 0;
  }

  .bf-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(20, 16, 33, 0.45);
    z-index: 200;
  }
}
</style>
